import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Stack,
  Paper,
  Divider,
  Tab,
  Tabs,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Zoom
} from '@mui/material';
import {
  ArticleRounded,
  DashboardRounded,
  PeopleRounded,
  SettingsRounded,
  SecurityRounded,
  SpeedRounded,
  CloudRounded,
  MobileFriendlyRounded,
  SearchRounded,
  NotificationsRounded,
  EmailRounded,
  ForumRounded,
  CampaignRounded,
  SellRounded,
  AnnouncementRounded,
  EditRounded,
  EditNotifications,
  TrendingUpRounded,
  SportsEsportsRounded,
  ExpandMoreRounded,
  CheckCircleRounded,
  StarRounded,
  CompareArrowsRounded,
  ApiRounded,
  IntegrationInstructionsRounded,
  RocketLaunchRounded,
  AutoAwesomeRounded,
  FlashOnRounded,
  ShieldRounded
} from '@mui/icons-material';

const ProductPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);
  const [expandedModule, setExpandedModule] = useState(false);
  const heroRef = useRef(null);
  const [isHeroVisible, setIsHeroVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsHeroVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  const coreModules = [
    {
      id: 'header-footer',
      title: 'Header & Footer Özelleştirme',
      icon: <SettingsRounded />,
      category: 'Temel',
      description: 'Tamamen özelleştirilebilir header ve footer tasarımları',
      features: ['Drag & Drop editör', '50+ hazır template', 'Responsive tasarım', 'Logo yönetimi'],
      techSpecs: 'React komponenti, CSS Grid, Flexbox',
      color: '#2563eb'
    },
    {
      id: 'page-types',
      title: '5 Farklı Sayfa Tipi',
      icon: <ArticleRounded />,
      category: 'İçerik',
      description: 'Anasayfa, kategori, haber detay, yazar ve arama sayfaları',
      features: ['Özelleştirilebilir layoutlar', 'SEO optimizasyonu', 'Mobil uyumlu', 'Hızlı yükleme'],
      techSpecs: 'Next.js, Server-side rendering, PWA desteği',
      color: '#059669'
    },
    {
      id: 'widgets',
      title: 'Sınırsız Widget Seçenekleri',
      icon: <DashboardRounded />,
      category: 'Tasarım',
      description: 'Modüler widget sistemi ile sınırsız sayfa tasarımları',
      features: ['100+ hazır widget', 'Özel widget geliştirme', 'Conditional rendering', 'A/B testing'],
      techSpecs: 'Mikro-frontend architecture, Lazy loading',
      color: '#dc2626'
    },
    {
      id: 'subscription',
      title: 'Abonelik Servisleri',
      icon: <PeopleRounded />,
      category: 'Kullanıcı',
      description: 'Kapsamlı üyelik ve abonelik yönetim sistemi',
      features: ['Premium içerik', 'Ödeme entegrasyonu', 'Kullanıcı segmentasyonu', 'Otomatik faturalama'],
      techSpecs: 'Stripe, PayPal, İyzico entegrasyonu',
      color: '#7c3aed'
    },
    {
      id: 'mail-management',
      title: 'Mail Yönetimi',
      icon: <EmailRounded />,
      category: 'İletişim',
      description: 'Profesyonel e-posta yönetim ve otomasyon sistemi',
      features: ['Newsletter sistemi', 'Otomatik e-postalar', 'Segmentasyon', 'A/B testing'],
      techSpecs: 'SendGrid, Mailgun, AWS SES entegrasyonu',
      color: '#ea580c'
    },
    {
      id: 'campaign-system',
      title: 'Kampanya Yönetimi',
      icon: <CampaignRounded />,
      category: 'Marketing',
      description: 'Güçlü kampanya ve promosyon yönetim sistemi',
      features: ['Hedefli kampanyalar', 'Otomatik tetikleyiciler', 'ROI takibi', 'Multi-channel'],
      techSpecs: 'Event-driven architecture, Real-time analytics',
      color: '#0891b2'
    }
  ];

  const extraModules = [
    {
      id: 'forum',
      title: 'Forum Sistemi',
      icon: <ForumRounded />,
      category: 'Topluluk',
      description: 'Okuyucu etkileşimi için modern forum sistemi',
      features: ['Threaded tartışmalar', 'Moderasyon araçları', 'Gamification', 'Real-time chat'],
      techSpecs: 'WebSocket, Redis, MongoDB',
      color: '#16a34a'
    },
    {
      id: 'classified-ads',
      title: 'İlan Sistemi',
      icon: <SellRounded />,
      category: 'Ticaret',
      description: 'Entegre ilan ve pazaryeri sistemi',
      features: ['Kategori yönetimi', 'Ödeme sistemi', 'Mesajlaşma', 'Coğrafi filtreleme'],
      techSpecs: 'Elasticsearch, Payment gateway, Maps API',
      color: '#ca8a04'
    },
    {
      id: 'blog-system',
      title: 'Blog Sistemi',
      icon: <EditRounded />,
      category: 'İçerik',
      description: 'Profesyonel blog ve köşe yazısı sistemi',
      features: ['Rich text editor', 'Medya galerisi', 'Yorum sistemi', 'Social sharing'],
      techSpecs: 'WYSIWYG editor, CDN entegrasyonu',
      color: '#be185d'
    },
    {
      id: 'announcements',
      title: 'Duyuru Sistemi',
      icon: <AnnouncementRounded />,
      category: 'İletişim',
      description: 'Dinamik duyuru ve bildirim sistemi',
      features: ['Push notifications', 'Email alerts', 'SMS entegrasyonu', 'Scheduling'],
      techSpecs: 'FCM, WebPush API, Cron jobs',
      color: '#9333ea'
    }
  ];

  const extraServices = [
    {
      id: 'headline-editor',
      title: 'Manşet Tasarım Editörü',
      icon: <AutoAwesomeRounded />,
      category: 'Editör',
      description: 'AI destekli manşet ve görsel tasarım editörü',
      features: ['AI öneriler', 'Template galerisi', 'Real-time preview', 'Brand compliance'],
      techSpecs: 'Canvas API, AI/ML models, Image processing',
      color: '#dc2626'
    },
    {
      id: 'weather-service',
      title: 'Hava Durumu Servisi',
      icon: <EditNotifications />,
      category: 'Widget',
      description: 'Gerçek zamanlı hava durumu ve tahmin servisi',
      features: ['5 günlük tahmin', 'Harita entegrasyonu', 'Uyarı sistemi', 'Mobil widget'],
      techSpecs: 'OpenWeather API, Geolocation, PWA',
      color: '#0ea5e9'
    },
    {
      id: 'stock-crypto',
      title: 'Borsa & Kripto Para',
      icon: <TrendingUpRounded />,
      category: 'Finans',
      description: 'Canlı borsa ve kripto para takip sistemi',
      features: ['Real-time data', 'Grafik analizi', 'Portföy takibi', 'Alarm sistemi'],
      techSpecs: 'WebSocket, Financial APIs, Chart.js',
      color: '#059669'
    },
    {
      id: 'sports-service',
      title: 'Spor Servisi',
      icon: <SportsEsportsRounded />,
      category: 'Spor',
      description: 'Canlı skor ve spor haberleri servisi',
      features: ['Canlı skorlar', 'Fikstür takibi', 'İstatistikler', 'Video highlights'],
      techSpecs: 'Sports APIs, Video streaming, Real-time updates',
      color: '#ea580c'
    }
  ];

  const systemFeatures = [
    {
      title: 'AMP Desteği',
      description: 'Google AMP ile ultra hızlı mobil deneyim',
      icon: <FlashOnRounded />,
      color: '#0891b2'
    },
    {
      title: 'CDN Entegrasyonu',
      description: 'Global CDN ile dünya çapında hızlı erişim',
      icon: <CloudRounded />,
      color: '#059669'
    },
    {
      title: 'SEO Optimizasyonu',
      description: 'Gelişmiş SEO araçları ve otomatik optimizasyon',
      icon: <SearchRounded />,
      color: '#7c3aed'
    },
    {
      title: 'Güvenlik Sistemi',
      description: 'Enterprise seviye güvenlik ve DDoS koruması',
      icon: <ShieldRounded />,
      color: '#dc2626'
    }
  ];

  const competitors = [
    {
      name: 'Bizim Sistem',
      modules: 18,
      customization: 'Sınırsız',
      support: '7/24',
      price: 'Esnek',
      score: 10,
      color: '#059669'
    },
    {
      name: 'Rakip A',
      modules: 8,
      customization: 'Sınırlı',
      support: 'İş saatleri',
      price: 'Sabit',
      score: 6,
      color: '#6b7280'
    },
    {
      name: 'Rakip B',
      modules: 12,
      customization: 'Orta',
      support: 'Ticket',
      price: 'Pahalı',
      score: 7,
      color: '#6b7280'
    }
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleModuleExpand = (panel) => (event, isExpanded) => {
    setExpandedModule(isExpanded ? panel : false);
  };

  const allModules = [...coreModules, ...extraModules, ...extraServices];
  const categories = ['Tümü', 'Temel', 'İçerik', 'Tasarım', 'Kullanıcı', 'İletişim', 'Marketing', 'Topluluk', 'Ticaret', 'Editör', 'Widget', 'Finans', 'Spor'];

  const filteredModules = activeTab === 0 
    ? allModules 
    : allModules.filter(module => module.category === categories[activeTab]);

  return (
    <Box>
      {/* Hero Section */}
      <Box
        ref={heroRef}
        sx={{
          minHeight: '60vh',
          background: `
            linear-gradient(135deg, #0f0f23 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #0e4b99 100%),
            radial-gradient(circle at 20% 50%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.2) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(120, 219, 255, 0.2) 0%, transparent 50%)
          `,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              repeating-linear-gradient(
                90deg,
                transparent,
                transparent 98px,
                rgba(255, 255, 255, 0.03) 100px
              ),
              repeating-linear-gradient(
                180deg,
                transparent,
                transparent 98px,
                rgba(255, 255, 255, 0.03) 100px
              )
            `,
            zIndex: 1
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in={isHeroVisible} timeout={1000}>
                <Box>
                  <Chip
                    label="🚀 ÜRÜNLERİMİZ"
                    sx={{
                      mb: 3,
                      background: 'linear-gradient(45deg, #ff6b6b, #4ecdc4)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      px: 2,
                      py: 0.5,
                      animation: 'pulse 2s infinite'
                    }}
                  />
                  
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '2.5rem', md: '4rem' },
                      fontWeight: 800,
                      background: 'linear-gradient(45deg, #ffffff, #a78bfa, #60a5fa)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 3,
                      lineHeight: 1.2
                    }}
                  >
                    18 Modül ile
                    <br />
                    <span style={{ 
                      background: 'linear-gradient(45deg, #f59e0b, #ef4444)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      Sınırsız Güç
                    </span>
                  </Typography>

                  <Typography
                    variant="h5"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      mb: 4,
                      fontWeight: 300,
                      lineHeight: 1.6
                    }}
                  >
                    Haber sitenizi hayal ettiğiniz gibi oluşturun. Temel modüllerden 
                    ekstra servislere kadar her şey tek platformda!
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<RocketLaunchRounded />}
                      sx={{
                        background: 'linear-gradient(45deg, #ff6b6b, #4ecdc4)',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        borderRadius: '50px',
                        boxShadow: '0 8px 32px rgba(255, 107, 107, 0.3)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 40px rgba(255, 107, 107, 0.4)'
                        }
                      }}
                    >
                      Modülleri Keşfet
                    </Button>
                    
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<CompareArrowsRounded />}
                      sx={{
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        borderRadius: '50px',
                        '&:hover': {
                          borderColor: 'white',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      Karşılaştır
                    </Button>
                  </Stack>
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Slide direction="left" in={isHeroVisible} timeout={1200}>
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  {/* Central Hub */}
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      background: 'linear-gradient(45deg, #ff6b6b, #4ecdc4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      zIndex: 3,
                      boxShadow: '0 0 50px rgba(255, 107, 107, 0.5)',
                      animation: 'pulse 3s infinite'
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{ color: 'white', fontWeight: 800 }}
                    >
                      18
                    </Typography>
                  </Box>

                  {/* Orbiting Modules */}
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'absolute',
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        background: `linear-gradient(45deg, ${
                          ['#2563eb', '#059669', '#dc2626', '#7c3aed', '#ea580c', '#0891b2'][index]
                        }, ${
                          ['#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#f97316', '#0ea5e9'][index]
                        })`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transform: `rotate(${index * 60}deg) translateX(150px) rotate(-${index * 60}deg)`,
                        animation: `orbit 20s linear infinite`,
                        animationDelay: `${index * 0.5}s`,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                        color: 'white',
                        fontSize: '1.5rem'
                      }}
                    >
                      {[
                        <ArticleRounded />,
                        <DashboardRounded />,
                        <SettingsRounded />,
                        <PeopleRounded />,
                        <EmailRounded />,
                        <CampaignRounded />
                      ][index]}
                    </Box>
                  ))}
                </Box>
              </Slide>
            </Grid>
          </Grid>
        </Container>

        <style jsx>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.8; transform: scale(1.05); }
          }
          
          @keyframes orbit {
            from { transform: rotate(0deg) translateX(150px) rotate(0deg); }
            to { transform: rotate(360deg) translateX(150px) rotate(-360deg); }
          }
        `}</style>
      </Box>

      {/* Module Categories */}
      <Box sx={{ py: 8, background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Chip
              label="📋 MODÜL KATEGORİLERİ"
              sx={{
                mb: 3,
                background: 'linear-gradient(45deg, #4f46e5, #06b6d4)',
                color: 'white',
                fontWeight: 600,
                px: 2
              }}
            />
            <Typography variant="h3" fontWeight={700} color="#1e293b" mb={2}>
              İhtiyacınız Olan Her Şey
            </Typography>
            <Typography variant="h6" color="#64748b" maxWidth="600px" mx="auto">
              18 farklı modül ile haber sitenizi istediğiniz gibi özelleştirin
            </Typography>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: '#64748b',
                  '&.Mui-selected': {
                    color: '#4f46e5'
                  }
                }
              }}
            >
              {categories.map((category, index) => (
                <Tab key={index} label={category} />
              ))}
            </Tabs>
          </Box>

          <Grid container spacing={3}>
            {filteredModules.map((module, index) => (
              <Grid item xs={12} md={6} lg={4} key={module.id}>
                <Zoom in timeout={300 + index * 100}>
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: 4,
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 40px ${module.color}20`,
                        borderColor: module.color
                      }
                    }}
                    onClick={() => handleModuleExpand(module.id)(null, expandedModule !== module.id)}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Avatar
                          sx={{
                            background: `linear-gradient(45deg, ${module.color}, ${module.color}dd)`,
                            mr: 2,
                            width: 48,
                            height: 48
                          }}
                        >
                          {module.icon}
                        </Avatar>
                        <Box flex={1}>
                          <Typography variant="h6" fontWeight={600} color="#1e293b">
                            {module.title}
                          </Typography>
                          <Chip
                            label={module.category}
                            size="small"
                            sx={{
                              backgroundColor: `${module.color}15`,
                              color: module.color,
                              fontWeight: 500
                            }}
                          />
                        </Box>
                      </Box>

                      <Typography variant="body2" color="#64748b" mb={3}>
                        {module.description}
                      </Typography>

                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} color="#1e293b" mb={1}>
                          Özellikler:
                        </Typography>
                        {module.features.slice(0, 2).map((feature, idx) => (
                          <Box key={idx} display="flex" alignItems="center" mb={0.5}>
                            <CheckCircleRounded sx={{ fontSize: 16, color: module.color, mr: 1 }} />
                            <Typography variant="body2" color="#64748b">
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                        {module.features.length > 2 && (
                          <Typography variant="body2" color={module.color} fontWeight={500}>
                            +{module.features.length - 2} özellik daha
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* System Features */}
      <Box sx={{ py: 8, background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Chip
              label="⚡ SİSTEM ÖZELLİKLERİ"
              sx={{
                mb: 3,
                background: 'linear-gradient(45deg, #f59e0b, #ef4444)',
                color: 'white',
                fontWeight: 600,
                px: 2
              }}
            />
            <Typography variant="h3" fontWeight={700} color="white" mb={2}>
              Enterprise Seviye Teknoloji
            </Typography>
            <Typography variant="h6" color="rgba(255, 255, 255, 0.7)" maxWidth="600px" mx="auto">
              Modern web teknolojileri ile güçlendirilmiş sistem
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {systemFeatures.map((feature, index) => (
              <Grid item xs={12} md={6} lg={3} key={index}>
                <Fade in timeout={800 + index * 200}>
                  <Paper
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      background: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 4,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: `0 15px 35px ${feature.color}30`,
                        borderColor: feature.color
                      }
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        mx: 'auto',
                        mb: 2,
                        background: `linear-gradient(45deg, ${feature.color}, ${feature.color}dd)`
                      }}
                    >
                      {feature.icon}
                    </Avatar>
                    <Typography variant="h6" fontWeight={600} color="white" mb={1}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                      {feature.description}
                    </Typography>
                  </Paper>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Comparison Table */}
      <Box sx={{ py: 8, background: 'linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Chip
              label="📊 KARŞILAŞTIRMA"
              sx={{
                mb: 3,
                background: 'linear-gradient(45deg, #059669, #0891b2)',
                color: 'white',
                fontWeight: 600,
                px: 2
              }}
            />
            <Typography variant="h3" fontWeight={700} color="#1e293b" mb={2}>
              Rakiplerimizle Karşılaştırın
            </Typography>
            <Typography variant="h6" color="#64748b" maxWidth="600px" mx="auto">
              Neden bizim sistemi tercih etmelisiniz?
            </Typography>
          </Box>

          <TableContainer component={Paper} sx={{ borderRadius: 4, overflow: 'hidden' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1e293b, #334155)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: '1.1rem' }}>
                    Özellik
                  </TableCell>
                  {competitors.map((competitor) => (
                    <TableCell 
                      key={competitor.name} 
                      align="center"
                      sx={{ 
                        color: 'white', 
                        fontWeight: 600, 
                        fontSize: '1.1rem',
                        background: competitor.name === 'Bizim Sistem' ? 'linear-gradient(45deg, #059669, #0891b2)' : 'transparent'
                      }}
                    >
                      {competitor.name}
                      {competitor.name === 'Bizim Sistem' && (
                        <StarRounded sx={{ ml: 1, color: '#fbbf24' }} />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  { label: 'Modül Sayısı', key: 'modules' },
                  { label: 'Özelleştirme', key: 'customization' },
                  { label: 'Destek', key: 'support' },
                  { label: 'Fiyatlandırma', key: 'price' }
                ].map((row) => (
                  <TableRow key={row.key} sx={{ '&:nth-of-type(even)': { backgroundColor: '#f8fafc' } }}>
                    <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                      {row.label}
                    </TableCell>
                    {competitors.map((competitor) => (
                      <TableCell 
                        key={competitor.name} 
                        align="center"
                        sx={{
                          fontWeight: competitor.name === 'Bizim Sistem' ? 600 : 400,
                          color: competitor.name === 'Bizim Sistem' ? competitor.color : '#64748b'
                        }}
                      >
                        {competitor[row.key]}
                        {competitor.name === 'Bizim Sistem' && (
                          <CheckCircleRounded sx={{ ml: 1, color: competitor.color, fontSize: 20 }} />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: 8,
          background: `
            linear-gradient(135deg, #0f0f23 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #0e4b99 100%),
            radial-gradient(circle at 30% 40%, rgba(120, 119, 198, 0.4) 0%, transparent 50%),
            radial-gradient(circle at 70% 60%, rgba(255, 119, 198, 0.3) 0%, transparent 50%)
          `,
          position: 'relative',
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            fontWeight={700}
            sx={{
              background: 'linear-gradient(45deg, #ffffff, #a78bfa)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 3
            }}
          >
            Hemen Başlayın!
          </Typography>
          
          <Typography
            variant="h6"
            sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 4, lineHeight: 1.6 }}
          >
            18 modül ile haber sitenizi hayalinizdeki gibi oluşturun. 
            Ücretsiz demo ile sistemi keşfedin!
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              startIcon={<RocketLaunchRounded />}
              sx={{
                background: 'linear-gradient(45deg, #ff6b6b, #4ecdc4)',
                color: 'white',
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 600,
                borderRadius: '50px',
                boxShadow: '0 8px 32px rgba(255, 107, 107, 0.3)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(255, 107, 107, 0.4)'
                }
              }}
            >
              Ücretsiz Demo
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              startIcon={<ApiRounded />}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: 'white',
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                borderRadius: '50px',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              API Dokümantasyonu
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default ProductPage;
